import { useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, Spin, Typography, message } from "antd";
import dayjs from "dayjs";

import { HabitStatus, type HabitTemplateEntity } from "@fitness-app/data-models/entities/Habit";

import { TraineeTargetingPanel, useTraineeTargeting } from "~/components/TraineeTargeting";
import { emptyTraineeTargetSpec, type TraineeTargetSpec } from "~/components/TraineeTargeting/types";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";

type Props = {
  open: boolean;
  habit: HabitTemplateEntity | null;
  onClose: () => void;
};

export const HabitAssignModal: FunctionComponent<Props> = ({ open, habit, onClose }) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const [spec, setSpec] = useState<TraineeTargetSpec>(emptyTraineeTargetSpec);

  const assignedQuery = useQuery({
    queryKey: ["habit-assigned-trainees", habit?.id],
    enabled: open && Boolean(habit?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_habit")
        .select("traineeId")
        .eq("templateId", habit?.id ?? "")
        .returns<{ traineeId: string }[]>();

      if (error) {
        throw error;
      }

      return data?.map((row) => row.traineeId) ?? [];
    },
  });

  // Seed the audience with the trainees already assigned so they show as pre-selected.
  // Only once per modal open — background refetches must not clobber in-progress edits.
  const seededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && assignedQuery.data && !seededRef.current) {
      seededRef.current = true;
      setSpec({ ...emptyTraineeTargetSpec, manualIds: assignedQuery.data });
    }
  }, [open, assignedQuery.data]);

  const { resolved, trainees, isLoading: targetingLoading } = useTraineeTargeting(spec, open);
  const traineeById = useMemo(() => new Map(trainees.map((trainee) => [trainee.id, trainee])), [trainees]);

  const assignMutation = useMutation({
    mutationFn: async (selectedTraineeIds: string[]) => {
      if (!habit) {
        return;
      }

      const initiallyAssigned = assignedQuery.data ?? [];
      const toAssign = selectedTraineeIds.filter((id) => !initiallyAssigned.includes(id));
      const toUnassign = initiallyAssigned.filter((id) => !selectedTraineeIds.includes(id));

      if (toAssign.length > 0) {
        const today = dayjs().format("YYYY-MM-DD");
        const rows = toAssign.map((traineeId) => ({
          templateId: habit.id,
          traineeId,
          userId: traineeById.get(traineeId)?.userId ?? null,
          createdBy: memberId ?? null,
          status: HabitStatus.Active,
          startDate: today,
        }));

        const { error } = await supabase
          .from("client_habit")
          .upsert(rows as never, { onConflict: "templateId,traineeId", ignoreDuplicates: true });

        if (error) {
          throw new Error(error.message);
        }
      }

      if (toUnassign.length > 0) {
        const { error } = await supabase
          .from("client_habit")
          .delete()
          .eq("templateId", habit.id)
          .in("traineeId", toUnassign);

        if (error) {
          throw new Error(error.message);
        }
      }
    },
    onSuccess: () => {
      void message.success("Przypisania zaktualizowane");
      void queryClient.invalidateQueries({ queryKey: ["habits-list"] });
      void queryClient.invalidateQueries({ queryKey: ["habit-assigned-trainees", habit?.id] });
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać przypisań: ${error.message}`);
    },
  });

  const isLoading = assignedQuery.isLoading || targetingLoading;

  return (
    <Modal
      title={<Typography.Text>Przypisz{habit ? `: ${habit.name}` : ""}</Typography.Text>}
      open={open}
      onCancel={onClose}
      onOk={() => assignMutation.mutate(resolved.map((trainee) => trainee.id))}
      okText="Zapisz przypisania"
      cancelText="Anuluj"
      confirmLoading={assignMutation.isLoading}
      okButtonProps={{ disabled: resolved.length === 0 }}
      width={680}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <TraineeTargetingPanel value={spec} onChange={setSpec} />
      )}
    </Modal>
  );
};

export default HabitAssignModal;
